import type { MetricExplanation, MetricOrigin, MetricWarning } from '../contracts';
import { overallocatedFlow, unallocatedFlow } from '../warnings';

export interface SplitEdgeInput {
  edgeId: string;
  /** null means unknown; a manual/otherwise-known conversion is 0..1. */
  conversion: number | null;
}

export interface SplitEdgeResolution {
  conversion: number | null;
  origin: MetricOrigin | null;
  /** True when this edge's conversion could not be trusted (split invalid). */
  invalid: boolean;
  explanation?: MetricExplanation;
}

export interface SplitResolution {
  results: Map<string, SplitEdgeResolution>;
  warnings: MetricWarning[];
}

function passthrough(edges: readonly SplitEdgeInput[]): Map<string, SplitEdgeResolution> {
  const map = new Map<string, SplitEdgeResolution>();
  for (const edge of edges) {
    map.set(edge.edgeId, {
      conversion: edge.conversion,
      origin: edge.conversion === null ? null : 'manual',
      invalid: false,
    });
  }
  return map;
}

/**
 * SPEC-008 §42-46/§75-78/plan §14/§19 — resolves the outgoing edge
 * conversions for one source node's split. Never assumes an equal split
 * (METRIC-REQ-014) and never distributes a remainder across more than one
 * unknown edge.
 */
export function resolveOutgoingSplit(edges: readonly SplitEdgeInput[]): SplitResolution {
  if (edges.length === 0) {
    return { results: new Map(), warnings: [] };
  }

  if (edges.length === 1) {
    const [only] = edges;
    if (only!.conversion !== null) {
      return { results: passthrough(edges), warnings: [] };
    }
    // SPEC-008 §42 — single outgoing edge, no semantic reason to split.
    const explanation: MetricExplanation = {
      metric: 'edgeConversion',
      formula: 'single_outgoing_edge_default',
      inputs: {},
      result: 1,
    };
    const results = new Map<string, SplitEdgeResolution>([
      [only!.edgeId, { conversion: 1, origin: 'calculated', invalid: false, explanation }],
    ]);
    return { results, warnings: [] };
  }

  const known = edges.filter((edge) => edge.conversion !== null);
  const unknown = edges.filter((edge) => edge.conversion === null);
  const knownSum = known.reduce((sum, edge) => sum + edge.conversion!, 0);

  if (knownSum > 1) {
    // §46 invalid / overallocated — known edges keep their own value, but
    // the split as a whole cannot be trusted; unknown edges are not
    // resolved from an invalid basis.
    const results = passthrough(edges);
    for (const edge of unknown) {
      results.set(edge.edgeId, { conversion: null, origin: null, invalid: true });
    }
    return {
      results,
      warnings: [overallocatedFlow(undefined, edges.map((edge) => edge.edgeId))],
    };
  }

  if (unknown.length === 1) {
    // §44/§75/§78 split remainder — a zero remainder is a known zero, not unknown.
    const remainder = 1 - knownSum;
    const explanation: MetricExplanation = {
      metric: 'edgeConversion',
      formula: 'split_remainder',
      inputs: { knownSum },
      result: remainder,
    };
    const results = passthrough(known);
    results.set(unknown[0]!.edgeId, { conversion: remainder, origin: 'calculated', invalid: false, explanation });
    return { results, warnings: [] };
  }

  if (unknown.length === 0) {
    const results = passthrough(edges);
    if (knownSum < 1) {
      // §42/AC-042 — allowed, but must warn rather than claim full allocation.
      return { results, warnings: [unallocatedFlow(undefined, edges.map((edge) => edge.edgeId))] };
    }
    return { results, warnings: [] };
  }

  // §40/§76 — more than one unknown: none are resolved, no equal split.
  return { results: passthrough(edges), warnings: [] };
}
